//=================================================
// Jumplinks
//=================================================
import { createInitializeOnce, getNavOffset, isAdmin } from './utilities';
import { getReducedMotion } from './user-preferences';

const initializeOnce = createInitializeOnce();

function scrollToAnchor(el) {
    const anchorOffset = (el.getBoundingClientRect().top + window.scrollY) - getNavOffset();
    const behavior = getReducedMotion() ? 'instant' : 'smooth';
    window.scrollTo({ top: anchorOffset, behavior: behavior });
    // Changing history state in admin is a little wonky with Bootstrap modals (things can shift underneath when the modal opens)
    if (!isAdmin()) {
        window.history.replaceState(window.history.state, document.title, `${ window.location.pathname }${ window.location.search }#${ el.id }`);
    }
}

function getLocalLinks() {
    const localLinks = [];
    const currentURL = new URL(window.location.href);

    // Don't include links in primary navigation
    document.querySelectorAll('main a:not(.footnote-indicator), .admin-jumplinks a').forEach(el => {
        const href = el.getAttribute('href');
        if (!href) return;

        let url;
        try {
            url = new URL(el.href, currentURL); // Resolve relative URLs
        } catch {
            return;
        }

        const match = url.hash ? document.getElementById(decodeURIComponent(url.hash.slice(1))) : null;
        // Check if the link is an anchor or a link to the current page and it matches something
        if (match && url.pathname === currentURL.pathname) {
            localLinks.push(el);
        }
    });

    return localLinks;
}

// Adjust scroll once the page has loaded since things may have moved
// Use getElementById since fragments like #2 aren't valid CSS selectors
function adjustAnchorScroll() {
    if (!window.location.hash) return;
    const el = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
    if (el) {
        const anchorOffset = (el.getBoundingClientRect().top + window.scrollY) - getNavOffset();
        window.scrollTo({ top: anchorOffset });
    }
}

// turbo:load fires once per navigation (including the initial load)
document.addEventListener('turbo:load', adjustAnchorScroll);

export default function setupJumplinks() {
    // Setup click handling for all local links
    const localLinks = getLocalLinks();
    localLinks.forEach((el) => {
        if (initializeOnce(el)) {
            el.addEventListener('click', (e) => {
                e.preventDefault();
                const url = new URL(el.href, window.location.href);
                const match = document.getElementById(decodeURIComponent(url.hash.slice(1)));
                if (match) {
                    scrollToAnchor(match);
                }
            });
        }
    });

    // Jumplinks rails have an active state based on scroll depth
    document.querySelectorAll('.jumplinks').forEach((wrapper) => {
        // Clean up existing observer if it exists
        if (wrapper.jumplinksObserver) {
            wrapper.jumplinksObserver.disconnect();
        }
        const links = [];
        const targets = [];

        wrapper.querySelectorAll('a').forEach((el) => {
            const match = document.getElementById(el.getAttribute('href').slice(1));
            if (match) {
                links.push(el);
                targets.push(match.offsetHeight > 0 ? match : match.parentElement);
            }
        });

        // Base what's active by the most visible element within the top 40% of the window
        if (targets.length > 0) {
            const observer = new IntersectionObserver((entries) => {
                let bestMatch = null;
                let maxIntersection = 0;
                const viewportHeight = window.innerHeight;
                const bottomBoundary = viewportHeight * 0.4; // 40% of viewport height

                entries.forEach(entry => {
                    const rect = entry.target.getBoundingClientRect();

                    // Only consider targets in the top 40% of the viewport
                    if (rect.top < bottomBoundary && rect.bottom > 0) {
                        const visibleHeight = Math.min(rect.bottom, bottomBoundary) - Math.max(rect.top, 0);
                        const intersectionRatio = visibleHeight / entry.target.offsetHeight;

                        if (intersectionRatio > maxIntersection) {
                            maxIntersection = intersectionRatio;
                            bestMatch = entry.target;
                        }
                    }
                });

                if (bestMatch) {
                    const index = targets.indexOf(bestMatch);
                    links.forEach(link => link.parentElement.classList.remove('active'));
                    links[index].parentElement.classList.add('active');
                }

            }, {
                threshold: 0,
                rootMargin: '-40% 0px -60% 0px'
            });

            targets.forEach(target => observer.observe(target));
            wrapper.jumplinksObserver = observer; // Store observer for cleanup
        }
    });
}
